if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupHeroNavigation();
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        this.updateHeroNavigation(event.detail.currentElement?.dataset.mediaId);
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setupHeroNavigation() {
        const viewer = this.elements.viewer;
        this.heroNavigation = this.querySelector('.ffs-gallery-nav');
        if (!this.heroNavigation) return;
        viewer.classList.add('ffs-gallery-has-nav');
        this.heroNavigation.querySelectorAll('[data-gallery-step]').forEach((button) => {
          button.addEventListener('click', () => this.moveHero(Number(button.dataset.galleryStep)));
        });
        this.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          if (!event.target.closest('.ffs-gallery-nav, .product__modal-opener--image')) return;
          event.preventDefault();
          this.moveHero(event.key === 'ArrowRight' ? 1 : -1);
        });
        // Phones retain the gallery's native swipe; wider screens also support drag/swipe.
        viewer.addEventListener('pointerdown', (event) => {
          if (!this.mql.matches || event.button !== 0 || !event.target.closest('.product__modal-opener--image')) return;
          this.heroPointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
        });
        viewer.addEventListener('pointerup', (event) => {
          const start = this.heroPointer;
          this.heroPointer = null;
          if (!start || start.id !== event.pointerId) return;
          const x = event.clientX - start.x;
          const y = event.clientY - start.y;
          if (Math.abs(x) < 45 || Math.abs(x) < Math.abs(y) * 1.5) return;
          this.suppressHeroClickUntil = performance.now() + 500;
          this.moveHero(x < 0 ? 1 : -1);
        });
        viewer.addEventListener('pointercancel', () => { this.heroPointer = null; });
        viewer.addEventListener('pointerleave', () => { this.heroPointer = null; });
        viewer.addEventListener('dragstart', (event) => {
          if (event.target.closest('.product__modal-opener--image')) event.preventDefault();
        });
        viewer.addEventListener('click', (event) => {
          if (event.target.closest('.product__modal-opener--image') && performance.now() < (this.suppressHeroClickUntil || 0)) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, true);
        this.updateHeroNavigation();
      }

      moveHero(step) {
        const media = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        if (media.length < 2) return;
        let index = media.findIndex((item) => item.classList.contains('is-active'));
        if (!this.mql.matches && Number.isFinite(this.elements.viewer.currentPage)) {
          index = this.elements.viewer.currentPage - 1;
        }
        const next = media[(Math.max(0, index) + step + media.length) % media.length];
        this.setActiveMedia(next.dataset.mediaId, false, true);
      }

      updateHeroNavigation(mediaId) {
        if (!this.heroNavigation) return;
        const media = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const index = media.findIndex((item) => mediaId ? item.dataset.mediaId === mediaId : item.classList.contains('is-active'));
        this.heroNavigation.hidden = media.length < 2;
        this.heroNavigation.querySelector('[data-gallery-current]').textContent = Math.max(0, index) + 1;
        this.heroNavigation.querySelector('[data-gallery-total]').textContent = media.length;
      }

      setActiveMedia(mediaId, prepend, preserveScroll = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.updateHeroNavigation();

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Gallery arrows and variant choices should keep the reader's position.
          if (preserveScroll || prepend) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
